import { SiteBrand } from "@/data/brand"
import { StarWars } from "@/data/star-wars"
import { DataTable, type Filter } from "@/modules/DataTable"
import { LayoutMain } from "@/modules/layout/main"
import {
	type TableColumn,
	type TableSort,
	TableSortDirection,
} from "@/modules/Table"

const columns: TableColumn<StarWars.Entry>[] = [
	{
		display: (row) => {
			let mediaLink = <></>
			if (row.mediaLink) {
				const mediaLinkResult = StarWars.getMediaLink(row.mediaLink)
				mediaLink = (
					<a
						className="media-link-icon"
						href={row.mediaLink}
						rel="noreferrer"
						target="_blank"
						title={mediaLinkResult.tooltipText}
					>
						<i className={mediaLinkResult.iconClass} />
					</a>
				)
			}

			return (
				<div className="canon-title">
					<a
						href={row.wookieepedia}
						rel="noreferrer"
						target="_blank"
						title="View on Wookieepedia"
					>
						{row.name}
					</a>
					{mediaLink}
				</div>
			)
		},
		name: "Name",
	},
	{
		display: (row) => {
			return StarWars.formatTimeline(row.timeline)
		},
		name: "Timeline",
		sortFunction: (rowA, rowB) => {
			return rowA.timeline > rowB.timeline ? 1 : -1
		},
	},
	{
		display: (row) => {
			if (!row.released) {
				return "TBD"
			}
			let label = row.released.getFullYear().toString()
			if (row.released >= new Date()) {
				label = row.released.toLocaleDateString()
			}

			return <span title={row.released.toLocaleDateString()}>{label}</span>
		},
		name: "Released",
		sortFunction: (a, b) => {
			const valueA = a.released || new Date("4000-01-01")
			const valueB = b.released || new Date("4000-01-01")

			return valueA > valueB ? 1 : -1
		},
	},
	{
		display: (row) => row.type,
		name: "Type",
	},
	{
		display: (row) => row.era,
		name: "Era",
	},
]

const defaultSort: TableSort = {
	columnIndex: 2,
	direction: TableSortDirection.DESC,
}

const filters: Record<string, Filter<StarWars.Entry>> = {
	era: {
		defaults: Object.values(StarWars.Era),
		filter: (row: StarWars.Entry, selectedValues: string[]): boolean => {
			return selectedValues.includes(row.era)
		},
		label: "Eras",
		options: StarWars.Era,
	},
	type: {
		defaults: [
			StarWars.Type.skywalkerSagaFilm,
			StarWars.Type.film,
			StarWars.Type.show,
		],
		filter: (row: StarWars.Entry, selectedValues: string[]): boolean => {
			return selectedValues.includes(row.type)
		},
		label: "Types",
		options: StarWars.Type,
	},
}

export const PageStarWars = () => {
	return (
		<LayoutMain Brand={SiteBrand}>
			<h1>Star Wars</h1>
			<h2>What is this?</h2>
			<p>
				This is a simple reference chart to all (most) Star Wars Canon media.
				It&apos;s not meant to be exhaustive list, instead it&apos;s meant to be
				quick reference for the high level overview of the majority of content
				out there.
			</p>
			<hr />
			<DataTable
				columns={columns}
				data={StarWars.Entries}
				defaultSort={defaultSort}
				filters={filters}
			/>
			<hr />
			<h2>Other Resources</h2>
			<p>
				The <a href="https://a.co/d/3Ayzvxd">Star Wars Timelines</a> book is a
				really good reference, put out by Lucasfilm, that covers all Star Wars
				Canon stories at the time of printing. But it&apos;s not clear which
				media the story comes from.
			</p>
			<p>
				<a href="https://youtini.com/timelines/canon">Youtini</a> offers
				update-to-date information on all Star Wars content. They specialize in
				reading material, but have information on other media as well. Totally
				worth checking out for a deeper dive than what is offered here.
			</p>
			<p>
				<a href="https://starwars.fandom.com/wiki/Timeline_of_canon_media">
					Wookieepedia
				</a>
				, maintained by a community of fans, has a much more extensive list of
				Star Wars Canon media. But can be a bit overwhelming.
			</p>
		</LayoutMain>
	)
}
